import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { requireAdmin, requireStaff } from "@/lib/admin-middleware";

export type ConsoleAccess = {
  userId: string;
  isAdmin: boolean;
  isEditor: boolean;
  roles: string[];
};

/**
 * Authoritative, server-evaluated answer to "may this session open the admin
 * console?". Route guards call this instead of trusting a browser-side query.
 */
export const fetchConsoleAccess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ConsoleAccess> => {
    const { data, error } = await context.supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    if (error) throw new Error("Forbidden: role lookup failed");
    const roles = (data ?? []).map((row) => row.role as string);
    return {
      userId: context.userId,
      isAdmin: roles.includes("admin"),
      isEditor: roles.includes("editor"),
      roles,
    };
  });

/** Throws unless the caller is an admin. */
export const assertAdminAccess = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => ({
    isAdmin: true as const,
    isEditor: context.isEditor,
    roles: context.roles,
  }));

/** Throws unless the caller is an admin or an editor. */
export const assertStaffAccess = createServerFn({ method: "POST" })
  .middleware([requireStaff])
  .handler(async ({ context }) => ({
    isAdmin: context.isAdmin,
    isEditor: context.isEditor,
    roles: context.roles,
  }));
